import {ConfirmationDialog, FormDialog, ReorderingPanel} from '@management-ui/core';
import {Delete, Edit, Scale} from '@mui/icons-material';
import numeral from 'numeral';
import React, {useCallback, useEffect, useState} from 'react';
import OrderItemForm from '../modules/admin/forms/OrderItemForm';
import WeightForm from '../modules/admin/forms/WeightForm';
import {formatCurrency} from '../utilities';

const OrderItems = ({order, onOrderUpdated, service}) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showWeight, setShowWeight] = useState(false);
  const [selected, setSelected] = useState({});

  const formatItems = useCallback((list) => (
    list.map((item) => ({
      entity: item,
      attributes: [
        [
          item.material?.name ?? '',
          item.format?.name ?? '',
          item.colour?.name ?? ''
        ].filter(d => !!d).join(' - '),
        item.price_tbc ? 'TBC' : (item.price ? formatCurrency(item.price, order.currency) : '-'),
        [
          ...(item.weight ? [
            `${numeral(item.weight).format('0,0.00')} MT`,
            ...(item.final_weight !== null ? [` (Final: ${numeral(item.final_weight).format('0,0.00')} MT)`] : [])
          ] : ['-'])
        ].join(' ')
      ]
    }))
  ), [order]);

  useEffect(() => {
    setItems(formatItems(order ? order.items : []));
  }, [order, formatItems]);

  const handleReorder = useCallback((reordered) => {
    setItems(formatItems(reordered));
    return service.item.reorderItems(order, reordered.map((item, index) => ({
      id: item.id,
      index: index + 1
    })));
  }, [order, service, formatItems]);

  const handleDelete = useCallback((confirmed) => {
    setShowDelete(false);
    if (confirmed) {
      setLoading(true);
      service.item.deleteItem(order, selected).then((updated) => {
        setLoading(false);
        onOrderUpdated(updated);
      }).catch(() => setLoading(false));
    }
  }, [order, onOrderUpdated, service, selected]);

  const handleSaved = useCallback((updated) => {
    onOrderUpdated(updated);
    setShowEdit(false);
    setShowWeight(false);
  }, [onOrderUpdated]);

  const handleWeight = useCallback(({weight}) => {
    return service.item.saveItem(order, {id: selected.id, final_weight: weight}).then((saved) => {
      handleSaved(saved);
    }).catch(() => null);
  }, [order, service, selected, handleSaved]);

  return (
    <>
      <ReorderingPanel
        title="Items"
        items={items}
        loading={loading}
        onLoading={setLoading}
        controls={order.archived || order.locked ? (
          !order.delivered_at ? [] : [
            {
              title: 'Add Final Weight',
              icon: <Scale/>,
              onClick: (item) => setSelected(item) || setShowWeight(true)
            },
          ]
        ) : [
          {
            title: 'Edit Item',
            icon: <Edit/>,
            onClick: (item) => setSelected(item) || setShowEdit(true)
          },
          {
            title: 'Delete Item',
            icon: <Delete/>,
            onClick: (item) => setSelected(item) || setShowDelete(true)
          }
        ]}
        onNew={order.archived || order.locked ? null : () => setSelected(null) || setShowEdit(true)}
        onReorder={order.archived || order.locked ? () => new Promise(resolve => resolve(order)) : handleReorder}
        onReordered={handleSaved}
      />
      <FormDialog
        title={selected ? 'Edit Item' : 'Add New Item'}
        open={showEdit}
        maxWidth="md"
        onClose={() => setShowEdit(false)}
        render={(props) => (
          <OrderItemForm
            {...props}
            service={service}
            order={order}
            item={selected ?? {}}
            onSaved={handleSaved}
          />
        )}
      />
      <FormDialog
        open={showWeight}
        title="Add Final Weight"
        maxWidth="sm"
        onClose={() => setShowWeight(false)}
        render={(props) => (
          <WeightForm
            {...props}
            entity={{weight: selected ? (selected.final_weight > 0 ? selected.final_weight : selected.weight) : ''}}
            onSave={handleWeight}/>
        )}
      />
      <ConfirmationDialog
        title="Delete Item"
        message="Are you sure you want to delete this item?"
        open={showDelete}
        onClose={handleDelete}
      />
    </>
  );
}

export default OrderItems;
